import React from 'react';
import { usePricing } from '../../contexts/PricingContext';
import { briefPricingTable as copy } from '../../data/partnershipBrief';
import { useFinancialModel } from '../../hooks/useFinancialModel';
import { day15Share, day45Share, getPricing, RECRUITER_MONTHLY_FEE } from '../../utils/roiCalculations';
const usd = (value: number) => '$' + Math.round(value).toLocaleString('en-US');
const pctOf = (value: number) => Math.min(1, Math.max(0, (value || 0) / 100));

/** Ad spend tiers beside the monthly service and projected output at each,
 *  with the client's own tier highlighted. Replaces separate scaling copy:
 *  reading down the table shows how the service grows with ad spend. */
export function BriefPricingTable({
  hideFootnote = false
}: {
  hideFootnote?: boolean;
} = {}) {
  const {
    adSpend,
    cpaLow,
    cpaHigh,
    platformFee,
    campaignFee,
    adSpendChunks,
    hasRecruiter,
    hasRetentionBonuses
  } = usePricing();
  const {
    inputs
  } = useFinancialModel();
  const pricing = getPricing(inputs);
  const launchRate = pctOf(inputs.pctLaunched);
  const pct30 = pctOf(inputs.pct30);
  const pct90 = pctOf(inputs.pct90);
  const pct60 = Math.sqrt(pct90);
  // Estimated placement bonuses for a month's hires, with the same formula as
  // the model: day 15 (and Flagship's day 45) counts rounded up, day 90 to
  // the nearest rep.
  const bonusesFor = (hires: number) => {
    const launched = hires * launchRate;
    return Math.ceil(launched * day15Share(pct30)) * pricing.bonusDay15 + Math.ceil(launched * day45Share(pct30, pct60)) * pricing.bonusDay45 + Math.round(launched * pct30 * pct90) * pricing.bonusDay90;
  };

  // Same rules as the deck's pricing: a flat platform fee plus one block per
  // $2,500 of ad spend (and a recruiter block when added).
  const perBlock = campaignFee / adSpendChunks + (hasRecruiter ? RECRUITER_MONTHLY_FEE : 0);
  const rows = getTiers(adSpend).map(spend => {
    const service = platformFee + perBlock * Math.max(1, Math.ceil(spend / 2500));
    // Rounded down so the range never beats the cost-per-hire floor.
    const hiresLow = Math.max(1, Math.floor(spend / cpaHigh));
    const hiresHigh = Math.max(hiresLow + 1, Math.floor(spend / cpaLow));
    // All-in: service + ads + the estimated bonuses those hires earn.
    const allIn = (hires: number) => (service + spend + bonusesFor(hires)) / hires;
    return {
      spend,
      service,
      hiresLow,
      hiresHigh,
      costLow: allIn(hiresHigh),
      costHigh: allIn(hiresLow),
      isPlan: spend === adSpend
    };
  });
  const head = 'pb-1.5 text-left text-[11.5px] font-bold uppercase tracking-[0.12em] text-[#c5cdd3]';
  const cell = 'py-[5px] text-[14.5px]';
  return <div data-id="element-764" data-mp="18ayu6li72f4t">
      <table className="w-full border-collapse" data-id="element-765" data-mp="18ap3evpbs3y2">
        <thead data-id="element-766" data-mp="3nyysj3cwov21">
          <tr className="border-b border-[#33404a]" data-id="element-767" data-mp="3nyfbrpyzscpl">
            <th scope="col" className={`${head} pl-3`} data-id="element-768" data-mp="3sythh1d4fakg">
              {copy.adSpendHead}
            </th>
            <th scope="col" className={head} data-id="element-769" data-mp="3lf2g4nozjjta">
              {hasRecruiter ? copy.serviceHeadRecruiter : copy.serviceHead}
            </th>
            <th scope="col" className={head} data-id="element-770" data-mp="3gfzab116l9k9">
              {copy.hiresHead}
            </th>
            <th scope="col" className={`${head} pr-3 text-right`} data-id="element-771" data-mp="3ufqkeqoku5sz">
              {copy.costHead}
            </th>
          </tr>
        </thead>
        <tbody data-id="element-772" data-mp="31rl0g65ckku7">
          {rows.map(row => <tr key={row.spend} className={row.isPlan ? 'bg-[#1d2b27] [&>td:first-child]:rounded-l-md [&>td:last-child]:rounded-r-md' : 'border-b border-[#222d35] last:border-b-0'} data-id="element-773" data-mp="31sebhjbc9wi5">
              <td className={`${cell} pl-3 text-[#d7dde2]`} data-id="element-774" data-mp="36pmqjb16xkf6">
                <span className={row.isPlan ? 'font-bold text-white' : ''} data-id="element-775" data-mp="2j0tu7dgnpak1">
                  {usd(row.spend)}
                </span>
                {row.isPlan && <span className="ml-2 text-[11.5px] font-bold uppercase tracking-[0.1em] text-[#6fd6b0]" data-id="element-776" data-mp="2dzxrh381jug7">
                    {copy.yourPlan}
                  </span>}
              </td>
              <td className={`${cell} font-bold text-white`} data-id="element-777" data-mp="2wlpxlkoxmy2l">
                {usd(row.service)}
              </td>
              <td className={`${cell} brief-display font-extrabold ${row.isPlan ? 'text-[#6fd6b0]' : 'text-[#9ee3c8]'}`} data-id="element-778" data-mp="36dn6ulwdrfyo">
                {row.hiresLow}–{row.hiresHigh}
              </td>
              <td className={`${cell} pr-3 text-right text-[#d7dde2]`} data-id="element-779" data-mp="3fd0mxxf0f2sd">
                {usd(row.costLow)}–{usd(row.costHigh)}
              </td>
            </tr>)}
        </tbody>
      </table>
      <div className="mt-2.5 flex items-baseline justify-between gap-6 text-[13.5px] leading-snug" data-id="element-780" data-mp="3pbzj4gil95yh">
        {hideFootnote ? <span data-id="element-781" data-mp="1q1qa6wnp7fy3" /> : <p className="font-semibold text-[#6fd6b0]" data-id="element-782" data-mp="1uxylu02osr0i">
            {hasRetentionBonuses ? copy.bonusFlagship : copy.bonus}
          </p>}
        <p className="shrink-0 text-[#aab4bc]" data-id="element-783" data-mp="c4n9zjapt9fk">{copy.adsNote}</p>
      </div>
    </div>;
}

/** Four ad spend tiers that always include the client's own spend. */
function getTiers(adSpend: number): number[] {
  const base = [2500, 5000, 7500, 10000];
  if (adSpend > 10000) {
    const above = Math.ceil(adSpend / 2500) * 2500 + 2500;
    return [5000, 10000, adSpend, above];
  }
  if (base.includes(adSpend)) return base;
  let nearest = 0;
  base.forEach((step, i) => {
    if (Math.abs(step - adSpend) < Math.abs(base[nearest] - adSpend)) nearest = i;
  });
  const tiers = [...base];
  tiers[nearest] = adSpend;
  return tiers.sort((a, b) => a - b);
}